import { Flex, Grid, Stack, Text, Tooltip } from '@mantine/core';
import { getJourneyDuration, getRelativeAge } from './shared/relativeAge';
import { Patient } from '@medplum/fhirtypes';
import { formatDate } from '@/services/date';
import { kgToLbs, cmToFeetInches } from '@/services/patient';
import { totalWeightLossPercent } from '@bariendo/lib/bmi';
import { useBMIData } from '../context-hooks/useBMIData';
import { ORBIT_GRAY } from '@/theme/colors';

interface BMISectionProps {
  patient: Patient;
}

interface ValueWithDateProps {
  label: string;
  value: string;
  date?: string;
  labelSpan?: number;
}

// Per content-table canon: label 13px / 0.55 black / 400 Regular,
// content 14px / Black / 400 Regular. No bold default.
const GRID_STYLE = { width: '100%', maxWidth: '100%', overflow: 'hidden' };
const LABEL_COL_STYLE = { display: 'flex', alignItems: 'flex-start' as const, minHeight: '18px' };
const LABEL_STYLE = { fontSize: 13, lineHeight: '16px', color: ORBIT_GRAY };
const VALUE_STYLE = { fontSize: 14, lineHeight: '20px', fontWeight: 400, color: 'var(--mantine-color-black)' };
const META_STYLE = { fontSize: 12, lineHeight: '16px', fontWeight: 400, color: ORBIT_GRAY };

function ValueWithDate({ label, value, date, labelSpan = 3 }: ValueWithDateProps) {
  return (
    <Grid align="flex-start" style={GRID_STYLE}>
      <Grid.Col span={labelSpan} style={LABEL_COL_STYLE}>
        <Text style={LABEL_STYLE}>{label}</Text>
      </Grid.Col>
      <Grid.Col span={12 - labelSpan} style={LABEL_COL_STYLE}>
        {date ? (
          <Tooltip
            label={`As of ${formatDate(date, 'MMM D, YYYY')}`}
            withArrow
            position="top"
            openDelay={300}
            withinPortal
          >
            <Text style={VALUE_STYLE}>{value}</Text>
          </Tooltip>
        ) : (
          <Text style={VALUE_STYLE}>{value}</Text>
        )}
      </Grid.Col>
    </Grid>
  );
}

// Canon-aligned start-→-end range row. Compresses the legacy
// "Starting weight + Latest weight" two-row pattern into one row with an
// arrow narrative. The dates live in a tooltip on hover, not inline — keeps
// the row tight at 380px panel width and lets the eye lock onto the
// transformation (a brand-aligned read for a weight-loss surface). If only
// one endpoint exists, we show just that endpoint (no arrow).
interface MetricRangeProps {
  label: string;
  startValue?: string;
  endValue?: string;
  startDate?: string;
  endDate?: string;
  labelSpan?: number;
}

function MetricRange({ label, startValue, endValue, startDate, endDate, labelSpan = 3 }: MetricRangeProps) {
  if (!startValue && !endValue) return null;

  // Determine display + which date the "(N ago)" parenthetical refers to.
  // In arrow mode (both endpoints present) the parenthetical describes the
  // LATEST endpoint — no fallback to startDate, since the display shows two
  // endpoints and the parenthetical can only meaningfully reference one.
  // In single-endpoint mode the parenthetical describes whichever endpoint is
  // shown. If the relevant date is missing, the parenthetical is omitted —
  // the tooltip still surfaces whatever dates exist.
  let displayValue: string;
  let dateForRelativeAge: string | undefined;
  if (startValue && endValue) {
    displayValue = `${startValue} → ${endValue}`;
    dateForRelativeAge = endDate; // do NOT fall back to startDate in arrow mode
  } else if (endValue) {
    displayValue = endValue;
    dateForRelativeAge = endDate;
  } else {
    displayValue = startValue as string;
    dateForRelativeAge = startDate;
  }

  // Tooltip carries the precise dates of whichever endpoints have one — both,
  // either, or neither (in which case the tooltip is suppressed entirely).
  const tooltipParts: string[] = [];
  if (startDate) tooltipParts.push(`Starting ${formatDate(startDate, 'MMM D, YYYY')}`);
  if (endDate) tooltipParts.push(`Latest ${formatDate(endDate, 'MMM D, YYYY')}`);
  const tooltipLabel = tooltipParts.length > 0 ? tooltipParts.join(' · ') : undefined;

  const relativeAge = getRelativeAge(dateForRelativeAge);

  const valueElement = (
    <Flex gap={4} align="baseline" style={{ minWidth: 0 }}>
      <Text style={VALUE_STYLE}>{displayValue}</Text>
      {relativeAge && <Text style={META_STYLE}>({relativeAge})</Text>}
    </Flex>
  );

  return (
    <Grid align="flex-start" style={GRID_STYLE}>
      <Grid.Col span={labelSpan} style={LABEL_COL_STYLE}>
        <Text style={LABEL_STYLE}>{label}</Text>
      </Grid.Col>
      <Grid.Col span={12 - labelSpan} style={LABEL_COL_STYLE}>
        {tooltipLabel ? (
          <Tooltip label={tooltipLabel} withArrow position="top" openDelay={300} withinPortal>
            {valueElement}
          </Tooltip>
        ) : (
          valueElement
        )}
      </Grid.Col>
    </Grid>
  );
}

export function BMISection({ patient }: BMISectionProps) {
  const { bmiData, bmiDataLoading } = useBMIData(patient);

  if (bmiDataLoading) {
    return null;
  }

  // Show empty state if we don't have any data
  if (!bmiData.startingWeight && !bmiData.latestWeight && !bmiData.startingBMI && !bmiData.latestBMI) {
    return (
      <Text size="sm" c={ORBIT_GRAY}>
        No body measurements recorded
      </Text>
    );
  }

  // Calculate total weight loss
  const totalWeightLossLbs =
    bmiData.startingWeight && bmiData.latestWeight
      ? kgToLbs(bmiData.startingWeight.value) - kgToLbs(bmiData.latestWeight.value)
      : undefined;

  const startLbsForPct = bmiData.startingWeight !== undefined ? kgToLbs(bmiData.startingWeight.value) : undefined;
  const weightLossPercentage =
    totalWeightLossLbs !== undefined && bmiData.startingWeight && startLbsForPct !== undefined && startLbsForPct > 0
      ? totalWeightLossPercent(totalWeightLossLbs, startLbsForPct)
      : undefined;

  const journeyDuration = getJourneyDuration(bmiData.startingWeight?.date, bmiData.latestWeight?.date);

  return (
    <Stack gap={8}>
      {/* Canon: one column of content-table rows. Weight + BMI collapse into
          a "start → end" arrow narrative — saves vertical rows and tells the
          transformation story at a glance. Dates live in a hover tooltip,
          which keeps the row compact at 380px panel width. */}
      {totalWeightLossLbs !== undefined && weightLossPercentage !== undefined && (
        <Grid align="flex-start" style={GRID_STYLE}>
          <Grid.Col span={3} style={LABEL_COL_STYLE}>
            <Text style={LABEL_STYLE}>TWL</Text>
          </Grid.Col>
          <Grid.Col span={9} style={LABEL_COL_STYLE}>
            <Flex gap={4} align="baseline" style={{ minWidth: 0 }}>
              <Text style={VALUE_STYLE}>{`${totalWeightLossLbs} lb (${weightLossPercentage.toFixed(1)}%)`}</Text>
              {journeyDuration && <Text style={META_STYLE}>(over {journeyDuration})</Text>}
            </Flex>
          </Grid.Col>
        </Grid>
      )}
      {bmiData.height && <ValueWithDate label="Height" value={cmToFeetInches(bmiData.height.value)} />}
      {(bmiData.startingWeight || bmiData.latestWeight) && (
        <MetricRange
          label="Weight"
          startValue={bmiData.startingWeight ? `${kgToLbs(bmiData.startingWeight.value)} lb` : undefined}
          endValue={bmiData.latestWeight ? `${kgToLbs(bmiData.latestWeight.value)} lb` : undefined}
          startDate={bmiData.startingWeight?.date}
          endDate={bmiData.latestWeight?.date}
        />
      )}
      {(bmiData.startingBMI || bmiData.latestBMI) && (
        <MetricRange
          label="BMI"
          startValue={bmiData.startingBMI ? bmiData.startingBMI.value.toFixed(1) : undefined}
          endValue={bmiData.latestBMI ? bmiData.latestBMI.value.toFixed(1) : undefined}
          startDate={bmiData.startingBMI?.date}
          endDate={bmiData.latestBMI?.date}
        />
      )}
    </Stack>
  );
}
